<template>
  <NaviButtonDropdown
    :label="props.label"
    class="navi-button-dropdown-group"
    :class="{
      'is-active': isButtonActive,
    }"
  >
    <div
      v-if="props.topList.length > 0"
      class="navi-button-dropdown-group-top-block"
    >
      <ul class="navi-button-dropdown-group-top-list">
        <li
          v-for="{
            label: itemLabel,
            href,
            isActiveValidator: isListActiveValidator,
            ...attrs
          } in props.topList"
          :key="itemLabel"
        >
          <a
            :href="href"
            class="navi-button-dropdown-group-list-item"
            :class="{
              'is-active': isLinkActiveValidator({
                listPath: href,
                activeValidator: isListActiveValidator
              })
            }"
            v-bind="attrs"
          >
            {{ itemLabel }}
          </a>
        </li>
      </ul>
    </div>

    <div class="navi-button-dropdown-group-block">
      <div
        v-for="list in props.groupList"
        :key="list.groupName"
        class="navi-button-dropdown-group-list-block"
        :style="list.groupStyle"
      >
        <span class="navi-button-dropdown-group-list-group-name">
          {{ list.groupName }}
        </span>
        <ul :class="{ wrap: list.groupLinks.length >= 6 }">
          <li
            v-for="{
              label: itemLabel,
              href,
              isActiveValidator: isListActiveValidator,
              ...attrs
            } in list.groupLinks"
            :key="itemLabel"
          >
            <a
              :href="href"
              class="navi-button-dropdown-group-list-item"
              :class="{
                'is-active': isLinkActiveValidator({
                  listPath: href,
                  activeValidator: isListActiveValidator
                })
              }"
              v-bind="attrs"
            >
              {{ itemLabel }}
            </a>
          </li>
        </ul>
      </div>
    </div>
  </NaviButtonDropdown>
</template>

<script setup>
import { computed } from 'vue'

import NaviButtonDropdown from './NaviButtonDropdown.vue'

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  topList: {
    /**
     * @example
      [
        { label: '選股功能總覽', href: '/screeners' },
        { label: '選股功能總覽2', href: '/screeners2', isActiveValidator: ({ currentPath, listPath }) => currentPath !== listPath },
        { label: '選股功能總覽3', href: '/screeners3', target: '_blank', rel: 'nofollow noreferrer' },
      ]
    */
    type: Array,
    default: () => [],
    validator(value) {
      return value.every((option) => (
        ['label', 'href'].every((requiredKey) => (
          Object.keys(option).includes(requiredKey)
        ))
      ))
    },
  },
  groupList: {
    /**
     * @example
     [
       {
         groupName: '常用選股功能',
         groupLinks: [
           { label: '自訂選股', href: '/screeners/custom' },
           { label: '績優股清單', href: '/screeners/quality', isActiveValidator: ({ currentPath, listPath }) => currentPath === listPath } },
           { label: '績優股清單2', href: '/screeners/quality2', target: '_blank', rel: 'nofollow noreferrer' },
         ],
       },
       {
         groupName: ' 指標排行榜選股 ',
         groupStyle: { borderLeft: '1px soid #c3c3c3' },
         groupLinks: [
           { label: '月營收排行榜', href: '/screeners/revenues_ranking' },
           { label: '殖利率排行榜', href: '/screeners/dividend_yield_ranking', isActiveValidator: ({ currentPath, listPath }) => currentPath === listPath } },
           { label: '殖利率排行榜2', href: '/screeners/dividend_yield_ranking_2', target: '_blank', rel: 'nofollow noreferrer' },
         ],
       },
     ]
    */
    type: Array,
    required: true,
    validator(value) {
      return value.every((option) => (
        ['groupName', 'groupLinks'].every((requiredKey) => (
          Object.keys(option).includes(requiredKey)
        ))
      ))
    },
  },
})

const isLinkActiveValidator = ({ listPath, activeValidator }) => {
  const hasActiveValidator = typeof activeValidator === 'function'
  const currentPath = window.location.pathname

  return hasActiveValidator
    ? activeValidator({ currentPath, listPath })
    : listPath === currentPath
}

const isButtonActive = computed(() => (
  props.topList.some(({ href, isActiveValidator }) => (
    isLinkActiveValidator({ listPath: href, activeValidator: isActiveValidator })
  )) || props.groupList.some((groupListItem) => (
    groupListItem.groupLinks.some(({ href, isActiveValidator }) => (
      isLinkActiveValidator({ listPath: href, activeValidator: isActiveValidator })
    ))
  ))
))
</script>

<style scoped lang="scss">
.navi-button-dropdown-group {
  :deep(.navi-button-dropdown-wrapper) {
    left: -47px;
  }
  :deep(.navi-button-dropdown-pointer) {
    left: 75px;
  }

  .navi-button-dropdown-group-list-item {
    width: 100%;
    line-height: 36px;
    padding: 0 15px;
    display: block;
    border-radius: 3px;
    box-sizing: border-box;
    text-align: left;

    &.is-active {
      color: #0386f4;

      &:hover {
        color: #fff;
        background: #0386f4;
      }
    }
  }
}

.navi-button-dropdown-group-top-block {
  padding: 0 9px 9px;
  margin: 0 0 9px;
  border-bottom: 1px solid #e9e9e9;

  .navi-button-dropdown-group-list-item {
    font-size: 15px;
    font-weight: 700;
  }
}

.navi-button-dropdown-group-block {
  display: flex;
  overflow: hidden;
  padding: 0 9px;
}

.navi-button-dropdown-group-list-block {
  ul {
    width: 140px;

    &.wrap {
      width: 100%;
      display: grid;
      grid-template-columns: repeat(2, 140px);
    }
  }

  .navi-button-dropdown-group-list-group-name {
    display: block;
    line-height: 32px;
    padding: 0 15px;
    color: #939393;
    font-size: 14px;
    font-weight: 400;
    text-align: left;
  }

  .navi-button-dropdown-group-list-item {
    font-size: 15px;
    font-weight: 700;
  }
}
</style>
